import { cast, types } from "mobx-state-tree";

import { DamageGroupCode, emptyGroupCode } from "./DamageGroupCode";
import { DamageMainCode, emptyMainCode } from "./DamageMainCode";
import { DamageDetails } from "./DamageDetails";
import { emptyDamageMetadata } from "../../api-models/response/DamageMetadataResponse";
import { VADetection } from "../video-player/VideoPlayerService";
import { emptyVADetection } from "../video-player/VideoPlayerService";
import { DamageC1Code, emptyC1Code } from "./DamageC1Code";
import { DamageC2Code, emptyC2Code } from "./DamageC2Code";
import { DamageDetailsForCodesResponse } from "../../api-models/response/DamageDetailsForCodesResponse";
import { DynamicProperty } from "../../components/data-components/DynamicProperty";
import { translationService } from "../translation/TranslationService";
import { DamageC2CodeResponse } from "../../api-models/response/DamageMainCodeResponse";
import { FieldsFromBM } from "../../api-models/response/BluemetricFields";

const DamageCodesModel = types.model({
  allMainCodes: types.optional(types.array(types.frozen<DamageMainCode>()), []),
  mainCodes: types.optional(types.array(types.frozen<DamageMainCode>()), []),
  groupCodes: types.optional(types.array(types.frozen<DamageGroupCode>()), []),
  c1Codes: types.optional(types.array(types.frozen<DamageC1Code>()), []),
  c2Codes: types.optional(types.array(types.frozen<DamageC2Code>()), []),
  damageCharacteristicsProperties: types.optional(types.array(types.frozen<DynamicProperty>()), []),
  oldDamageCharacteristicsProperties: types.optional(
    types.array(types.frozen<DynamicProperty>()),
    []
  ),
  damageDetails: types.optional(
    types.frozen<DamageDetails>(),
    DamageDetails.create(emptyDamageMetadata)
  ),
  selectedVADetection: types.maybe(types.frozen<VADetection>()),
  selectedMainCode: types.maybeNull(types.frozen<DamageMainCode>()),
  initialMainCode: types.optional(types.frozen<DamageMainCode>(), emptyMainCode),
  selectedC1Code: types.optional(types.frozen<DamageC1Code>(), emptyC1Code),
  initialC1Code: types.optional(types.frozen<DamageC1Code>(), emptyC1Code),
  selectedC2Code: types.optional(types.frozen<DamageC2Code>(), emptyC2Code),
  initialC2Code: types.optional(types.frozen<DamageC2Code>(), emptyC2Code),
  selectedGroupCode: types.optional(types.frozen<DamageGroupCode>(), emptyGroupCode),
  oldMainCodeValue: "",
  oldC1CodeValue: "",
  oldC2CodeValue: "",
  mainCodeFieldIndicator: "",
  mainCodeLabel: "",
  c1CodeFieldIndicator: "",
  c1CodeLabel: "",
  c2CodeFieldIndicator: "",
  c2CodeLabel: "",
  q1CodeFieldIndicator: "",
  q2CodeFieldIndicator: "",
  characteristicsAreDisabled: false,
  newDamageCharacteristicsRequired: false,
  loadingDamageCharacteristics: false,
  c2CodesAreLoading: false,
});

export const DamageCodesStorageModel = DamageCodesModel.named("DamageCodesStorageModel")
  .views((self) => ({
    get isMainCodeSelected() {
      return self.selectedMainCode && self.selectedMainCode.code !== emptyMainCode.code;
    },
    get mainCodeHasC1Codes() {
      return self.selectedMainCode && self.selectedMainCode.hasC1Codes;
    },
    get isC1CodeSelected() {
      return self.selectedC1Code.code !== emptyC1Code.code;
    },
    get c1CodeHasC2Code() {
      return self.selectedC1Code.hasC2Code;
    },
    get isC2CodeSelected() {
      return self.selectedC2Code.code !== emptyC2Code.code;
    },
  }))
  .views((self) => ({
    get damageCodesAreSet() {
      const allDamageCodesAreRequiredAndSet =
        self.isMainCodeSelected &&
        self.mainCodeHasC1Codes &&
        self.isC1CodeSelected &&
        self.c1CodeHasC2Code &&
        self.isC2CodeSelected;

      const onlyMainCodeIsRequiredAndSet = self.isMainCodeSelected && !self.mainCodeHasC1Codes;

      const onlyMainCodeAndC1CodeAreRequiredAndSet =
        self.isMainCodeSelected &&
        self.mainCodeHasC1Codes &&
        self.isC1CodeSelected &&
        !self.c1CodeHasC2Code;

      return !!(
        allDamageCodesAreRequiredAndSet ||
        onlyMainCodeIsRequiredAndSet ||
        onlyMainCodeAndC1CodeAreRequiredAndSet
      );
    },
  }))
  .actions((self) => {
    return {
      setNewDamageCharacteristics(damageCharacteristics: any) {
        self.damageCharacteristicsProperties = damageCharacteristics;
      },
    };
  })
  .actions((self) => {
    return {
      mapC1CodeDynamicPropertyToDamageC1Code(
        damage: DamageDetails,
        selectedMainCode: DamageMainCode,
        selectedVADetection?: VADetection
      ): DamageC1Code {
        const c1CodeValue = damage.getDynamicPropertyByIndicator(
          damage.c1CodeFieldIndicator
        )?.value;

        const c1CodeForComparison = selectedVADetection ? selectedVADetection.c1Code : c1CodeValue;
        const c1Code = selectedMainCode.c1Codes?.find((x) => x.code === c1CodeForComparison);

        if (!c1Code || c1Code.code === emptyC1Code.code) return emptyC1Code;

        const emptyC1CodeText = translationService.translate("general.none");

        return new DamageC1Code(
          {
            code: c1Code.code,
            text: c1Code.code === "" ? emptyC1CodeText : c1Code.text,
            hasC2Code: c1Code.hasC2Code,
          },
          true
        );
      },
      setQuantificationFieldsDetails(
        damageDetails: DamageDetailsForCodesResponse,
        q1CodeFieldIndicator: string,
        q2CodeFieldIndicator: string
      ) {
        let q1Code = self.damageCharacteristicsProperties.find(
          (x) => x.metadata.indicator === q1CodeFieldIndicator
        );
        if (q1Code) {
          q1Code.setUnit(damageDetails.quantification1Unit);
          q1Code.setDescription(damageDetails.quantification1Text);
        }
        let q2Code = self.damageCharacteristicsProperties.find(
          (x) => x.metadata.indicator === q2CodeFieldIndicator
        );
        if (q2Code) {
          q2Code.setUnit(damageDetails.quantification2Unit);
          q2Code.setDescription(damageDetails.quantification2Text);
        }
      },
      markNecessaryDamageCharacteristicsAsReadonly(damageDetails: DamageDetailsForCodesResponse) {
        const allPropertiesEditable: DynamicProperty[] = self.damageCharacteristicsProperties.map(
          (x: DynamicProperty) => {
            x.markAsEditable();
            return x;
          }
        );

        damageDetails.disabledFields.forEach((disabledFieldIndicator) => {
          allPropertiesEditable
            .find((x: any) => x.metadata.indicator === disabledFieldIndicator)
            ?.markAsReadonly();
        });

        self.setNewDamageCharacteristics(allPropertiesEditable);
      },
      clearDisabledFields(damageDetails: DamageDetailsForCodesResponse) {
        damageDetails.disabledFields.forEach((disabledFieldIndicator) => {
          const index = self.damageCharacteristicsProperties.findIndex(
            (x) => x.metadata.indicator === disabledFieldIndicator
          );

          if (index >= 0)
            self.damageCharacteristicsProperties[index] = new DynamicProperty({
              value: "",
              metadata: self.damageCharacteristicsProperties[index].metadata,
              issues: self.damageCharacteristicsProperties[index].issues,
            });
        });
      },
      getC2Options(damage: DamageDetails): DamageC2Code[] {
        const c2Code = damage.getDynamicPropertyByIndicator(damage.c2CodeFieldIndicator);

        if (!c2Code) return [];
        const catalogOptions = c2Code.getCatalogOptions();

        return catalogOptions.map(
          (x) =>
            new DamageC2Code(
              {
                code: x.key,
                text: x.value.split(x.key)[1],
              },
              true
            )
        );
      },
      mapC2CodeDynamicPropertyToDamageC2Code(
        damage: DamageDetails,
        c2CodeOptions: DamageC2Code[]
      ): DamageC2Code {
        const c2Code = damage.getDynamicPropertyByIndicator(damage.c2CodeFieldIndicator);

        if (!c2Code || !c2Code.value || !c2CodeOptions.find((x) => x.code === c2Code.value))
          return emptyC2Code;

        return new DamageC2Code(
          {
            code: c2Code.value,
            text: c2Code.getCatalogValue()?.split(c2Code.value)[1] || "",
          },
          true
        );
      },
      mapMainCodeDynamicPropertyToDamageMainCode(
        damage: DamageDetails,
        allMainCodes: DamageMainCode[],
        selectedVADetection?: VADetection
      ): DamageMainCode {
        const mainCodeValue = damage.getDynamicPropertyByIndicator(
          damage.mainCodeFieldIndicator
        )?.value;

        const mainCodeForComparison = selectedVADetection
          ? selectedVADetection.mainCode
          : mainCodeValue;
        const mainCode = allMainCodes.find((x) => x.code === mainCodeForComparison);

        if (!mainCode) return emptyMainCode;

        return new DamageMainCode({
          code: mainCode.code,
          text: mainCode.text,
          c1Codes: mainCode.c1Codes,
          hasC1Codes: mainCode.hasC1Codes,
        });
      },
      getLabelBasedOnIndicator(damage: DamageDetails, indicator: string) {
        return damage.getDynamicPropertyByIndicator(indicator)?.metadata.name || "";
      },
      clearAllSelectedCodes() {
        self.selectedMainCode = emptyMainCode;
        self.selectedC1Code = emptyC1Code;
        self.c1Codes = cast([]);
        self.selectedC2Code = emptyC2Code;
        self.c2Codes = cast([]);
      },
    };
  })
  .actions((self) => {
    return {
      initialDataReceived(
        damageGroupCodes: DamageGroupCode[],
        damageDetails: DamageDetails,
        detectedDamage?: VADetection
      ) {
        self.groupCodes = cast([...damageGroupCodes]);

        const mainCodesFromGroupCodes: DamageMainCode[] = damageGroupCodes.flatMap(
          (x: DamageGroupCode) => x.mainCodes
        );

        self.allMainCodes = cast(mainCodesFromGroupCodes);
        self.mainCodes = cast(mainCodesFromGroupCodes);

        self.selectedVADetection = detectedDamage;
        const damageMainCode: DamageMainCode = self.mapMainCodeDynamicPropertyToDamageMainCode(
          damageDetails,
          self.allMainCodes,
          self.selectedVADetection
        );
        self.selectedMainCode = damageMainCode;
        self.initialMainCode = damageMainCode;
        self.oldMainCodeValue =
          damageDetails.getDynamicPropertyByIndicator(damageDetails.mainCodeFieldIndicator)
            ?.value || "";

        self.c1Codes = cast(self.selectedMainCode.c1Codes);

        const damageC1Code: DamageC1Code = self.mapC1CodeDynamicPropertyToDamageC1Code(
          damageDetails,
          self.selectedMainCode,
          self.selectedVADetection
        );
        self.selectedC1Code = damageC1Code;
        self.initialC1Code = damageC1Code;
        self.oldC1CodeValue =
          damageDetails.getDynamicPropertyByIndicator(damageDetails.c1CodeFieldIndicator)?.value ||
          "";

        const c2CodeOptions = self.getC2Options(damageDetails);
        self.c2Codes = cast(c2CodeOptions);
        const damageC2Code: DamageC2Code = self.mapC2CodeDynamicPropertyToDamageC2Code(
          damageDetails,
          c2CodeOptions
        );
        self.selectedC2Code = damageC2Code;
        self.initialC2Code = damageC2Code;
        self.oldC2CodeValue =
          damageDetails.getDynamicPropertyByIndicator(damageDetails.c2CodeFieldIndicator)?.value ||
          "";

        self.characteristicsAreDisabled = !self.damageCodesAreSet;

        self.mainCodeFieldIndicator = damageDetails.mainCodeFieldIndicator;
        self.mainCodeLabel = self.getLabelBasedOnIndicator(
          damageDetails,
          self.mainCodeFieldIndicator
        );

        self.c1CodeFieldIndicator = damageDetails.c1CodeFieldIndicator;
        self.c1CodeLabel = self.getLabelBasedOnIndicator(damageDetails, self.c1CodeFieldIndicator);

        self.c2CodeFieldIndicator = damageDetails.c2CodeFieldIndicator;
        self.c2CodeLabel = self.getLabelBasedOnIndicator(damageDetails, self.c2CodeFieldIndicator);

        self.q1CodeFieldIndicator = damageDetails.quantification1FieldIndicator;
        self.q2CodeFieldIndicator = damageDetails.quantification2FieldIndicator;

        const damageCharacteristics = damageDetails.getPropertiesForEditNotInList([
          self.mainCodeFieldIndicator,
          self.c1CodeFieldIndicator,
          self.c2CodeFieldIndicator,
        ]);

        self.damageCharacteristicsProperties = cast(damageCharacteristics);
        self.oldDamageCharacteristicsProperties = cast(damageCharacteristics);

        if (self.selectedVADetection)
          self.newDamageCharacteristicsRequired = self.damageCodesAreSet;

        if (self.selectedVADetection?.c1Code) self.c2CodesAreLoading = true;
      },
      groupCodeSelected(selectedGroupCode: DamageGroupCode) {
        self.selectedGroupCode = selectedGroupCode;
        if (selectedGroupCode) {
          self.mainCodes = cast(selectedGroupCode.mainCodes);
          if (
            !selectedGroupCode.mainCodes.find(
              (x: DamageMainCode) => x.code === self.selectedMainCode?.code
            )
          ) {
            self.clearAllSelectedCodes();
            self.characteristicsAreDisabled = true;
          }
        } else {
          self.mainCodes = cast([...self.allMainCodes]);
          self.clearAllSelectedCodes();
          self.characteristicsAreDisabled = true;
        }
      },
      mainCodeSelected(selectedMainCode: DamageMainCode | null) {
        self.selectedMainCode = selectedMainCode;
        self.characteristicsAreDisabled = true;
        self.selectedVADetection = emptyVADetection;

        if (!selectedMainCode) {
          self.clearAllSelectedCodes();
        } else {
          self.selectedC1Code = emptyC1Code;
          self.c1Codes = cast(self.selectedMainCode?.c1Codes ?? []);
          self.selectedC2Code = emptyC2Code;
        }
        self.newDamageCharacteristicsRequired = self.damageCodesAreSet;
      },
      c1CodeSelected(selectedC1Code: DamageC1Code) {
        self.selectedC1Code = selectedC1Code;
        self.selectedC2Code = emptyC2Code;
        self.c2Codes = cast([]);
        self.selectedVADetection = emptyVADetection;
        if (self.selectedC1Code.hasC2Code) {
          self.c2CodesAreLoading = true;
          self.characteristicsAreDisabled = true;
        }

        self.newDamageCharacteristicsRequired = self.damageCodesAreSet;
      },
      c2CodeSelected(selectedC2Code: DamageC2Code) {
        self.selectedC2Code = selectedC2Code;
        self.selectedVADetection = emptyVADetection;
        self.newDamageCharacteristicsRequired = self.damageCodesAreSet;
      },
      receivedC2Codes(c2Codes: DamageC2CodeResponse[]) {
        self.c2Codes = cast(c2Codes.map((x) => new DamageC2Code(x, true)));

        if (self.selectedVADetection?.c2Code) {
          self.selectedC2Code =
            self.c2Codes.find((x) => x.code === self.selectedVADetection?.c2Code) ?? emptyC2Code;
          self.newDamageCharacteristicsRequired = true;
        }
        self.c2CodesAreLoading = false;
      },
      setLoadingDamageCharacteristics() {
        self.loadingDamageCharacteristics = true;
        self.newDamageCharacteristicsRequired = false;
      },
      receivedDamageCharacteristics(damageDetails: DamageDetailsForCodesResponse) {
        self.newDamageCharacteristicsRequired = false;
        self.loadingDamageCharacteristics = false;
        self.characteristicsAreDisabled = false;
        self.markNecessaryDamageCharacteristicsAsReadonly(damageDetails);
        self.clearDisabledFields(damageDetails);
        self.setQuantificationFieldsDetails(
          damageDetails,
          self.q1CodeFieldIndicator,
          self.q2CodeFieldIndicator
        );
      },
      extractFieldsFromBM(items: DynamicProperty[]) {
        const extractedFields: FieldsFromBM = {};

        items.forEach((item) => {
          const id = item.metadata.indicator;
          const value = item.value ? String(item.value).trim() : null;

          if (!extractedFields[id]) extractedFields[id] = value;
        });

        return extractedFields;
      },
      getFieldAsBMField(fieldIndicator: string, fieldValue: any): FieldsFromBM {
        return { [fieldIndicator]: fieldValue };
      },
    };
  });
